// @flow
import moment from 'moment';
import React, { Component } from 'react';
import styled from 'styled-components';
import Card from 'material-ui/Card';
import RaisedButton from 'material-ui/RaisedButton';
import LinearProgress from 'material-ui/LinearProgress';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Radio from 'common/domain/radio';
import Page from 'common/components/Page';
import Images from 'statsradio/themes/Images';
import FixedActionBar from 'common/components/FixedActionsBar';
import PrintButton from 'statsradio/components/PrintButton';
import Location from 'common/helpers/location';
import PrintingService from 'common/services/PrintingService';

import { getToken, getRadio } from '../authentication/session/reducers/sessionReducer';
import HistoryDateRangeSelector from './components/HistoryDateRangeSelector';
import type { HistoryDateRangeType } from './domain/HistoryDateRange';
import * as HistoryDateRange from './domain/HistoryDateRange';
import HistoryGraph from './components/HistoryGraph';
import History from './domain/History';
import HistoryApi from './services/HistoryApi';
import HistoryList from './components/HistoryList';
import RadioCentralMarketDropdown from '../centralMarkets/components/RadioCentralMarketDropdown';
import FeaturePermission from '../../../common/components/FeaturePermission';
import { CentralMarket } from '../centralMarkets/domain/CentralMarket';

const WrapperPage = styled.div`
  display: flex;
`;

const CentralMarketWrapper = styled.div`
  width: 300px;
  margin-bottom: ${({ theme }) => theme.metrics.margin}px;
`;

const DateRangeWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.metrics.largeMargin}px;
`;

const SCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.metrics.actionBarHeight}px;
`;

const SCardContent = styled.div`
  display: flex;
  width: 100%;
  height: 450px;

  padding: ${({ theme }) => theme.metrics.largeMargin}px;

  & > * {
    flex: 1;
  }
`;

const PlaceholderImage = styled.img`
  width: 440px;
  height: 218px;
  margin: 0px auto;
  display: block;
`;

const HistoryListContainer = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.cardBackground};

  min-width: 320px;
  max-width: 320px;
  padding-bottom: ${({ theme }) => theme.metrics.navBarHeight}px;
  height: ${({ theme }) => `calc(100vh - ${theme.metrics.navBarHeight}px)`};

  box-shadow: ${({ theme }) => theme.colors.boxShadow};
`;

const DataWrapper = styled.div`
  display: flex;
  height: 100%;

  flex-direction: column;
  align-items: center;
`;

const ProgressWrapper = styled.div`
  top: 0;
  float: left;
  width: 100%;

  ${({ fetching }) => !fetching && `
    opacity: 0;
  `}
`;

const DataTitle = styled.span`
  font-size: 21px;
  margin-top: ${({ theme }) => theme.metrics.largeMargin}px;
`;

const DataContent = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;

  font-size: ${({ theme }) => theme.fonts.style.xsmall.fontSize}px;
  margin-top: ${({ theme }) => theme.metrics.margin}px;
`;

type Props = {
  radio: Radio;
  token: string;

  t: any;
}

type State = {
  history?: History;
  selectedDateRange: HistoryDateRangeType;
  selectedDate?: moment;
  centralMarket: ?CentralMarket;

  isFetching: boolean;
  hasError: boolean;
};

class HistoryPage extends Component<Props, State> {

  state = {
    selectedDateRange: 'lastMonth',
    selectedDate: undefined,
    history: undefined,
    isFetching: false,
    hasError: false,
    centralMarket: undefined,
  };

  componentDidMount() {
    this.fetchHistoryData();
  }

  componentDidUpdate(prevProps: Props) {
    const { radio } = this.props;
    const prevRadio = prevProps.radio;

    if (!radio.isSame(prevRadio)) {
      this.setState({ centralMarket: undefined }, this.fetchHistoryData);
    }
  }

  fetchHistoryData = () => {
    const { token, radio: { callSign } } = this.props;
    const { selectedDateRange, centralMarket } = this.state;

    this.setState({ isFetching: true });

    HistoryApi.fetchMetricsHistory(token, callSign, HistoryDateRange.asDateRange(selectedDateRange), centralMarket)
      .then(history => this.setState({ history, isFetching: false, hasError: false }))
      .catch(() => this.setState({ history: undefined, isFetching: false, hasError: true }));
  };

  onSelectDateRange = (selectedDateRange: HistoryDateRangeType) => {
    this.setState({ selectedDateRange }, this.fetchHistoryData);
  };

  onDateClick = (selectedDate: moment) => {
    if (!selectedDate) {
      return;
    }
    this.setState({ selectedDate: selectedDate.startOf('isoWeek') });
  };

  onRetry = () => {
    this.fetchHistoryData();
  }

  onPrint = () => {
    const { radio } = this.props;
    const { history, selectedDateRange, centralMarket } = this.state;

    const url = Location.buildUrlFor('history-printing');

    const message = {
      history: history && history.toJSON(),
      selectedDateRange: selectedDateRange,
      radio: radio.toJSON(),
      centralMarket: centralMarket,
    };

    PrintingService.printWindowWithData(url, 'HISTORY_PRINTING_DATA', message);
  };

  onCentralMarketChange = (newCentralMarket?: CentralMarket) => {
    this.setState({ centralMarket: newCentralMarket }, this.fetchHistoryData);
  }

  render() {
    const { t } = this.props;
    const { selectedDateRange, history, selectedDate, centralMarket, isFetching, hasError } = this.state;

    const displayError = hasError;
    const displayNoData = !isFetching && !displayError && history && history.isEmpty();
    const displayHistory = history && !displayError && !displayNoData;

    return (
      <WrapperPage>
        <Page title={t('history')}>
          <FeaturePermission feature="CENTRAL_MARKET_MODULE">
            <CentralMarketWrapper>
              <RadioCentralMarketDropdown
                selectedCentralMarket={centralMarket}
                onSelectCentralMarket={c => this.onCentralMarketChange(c)}
                withCard
              />
            </CentralMarketWrapper>
          </FeaturePermission>
          <DateRangeWrapper>
            <HistoryDateRangeSelector dateRanges={HistoryDateRange.allDateRanges()}
              selectedDateRange={selectedDateRange} onSelectDateRange={this.onSelectDateRange}
            />
          </DateRangeWrapper>
          <SCard>
            <ProgressWrapper fetching={isFetching}>
              <LinearProgress mode="indeterminate" />
            </ProgressWrapper>
            <SCardContent>
              {displayHistory &&
              <HistoryGraph data={history} dateRange={selectedDateRange} onGraphClick={this.onDateClick} animate />
              }
              {displayNoData &&
              <DataWrapper>
                <PlaceholderImage src={Images.history.empty}
                  alt={`${t('insufficientData')}. ${t('historyHasNoDataMessage')}`}
                />
                <DataTitle>{t('insufficientData')}</DataTitle>
                <DataContent>{t('historyHasNoDataMessage')}</DataContent>
              </DataWrapper>
              }
              {displayError &&
              <DataWrapper>
                <PlaceholderImage src={Images.history.error}
                  alt={`${t('insufficientData')}. ${t('historyHasNoDataMessage')}`}
                />
                <DataTitle>{t('error.title')}</DataTitle>
                <DataContent>
                  <p>{t('error.message')}</p>
                  <RaisedButton label={t('error.buttonRetry')} primary onClick={this.onRetry} />
                </DataContent>
              </DataWrapper>
              }
            </SCardContent>
          </SCard>
        </Page>
        <HistoryListContainer>
          <HistoryList
            data={history}
            selectedDate={selectedDate}
          />
        </HistoryListContainer>
        {displayHistory &&
        <FixedActionBar position="BOTTOM">
          <PrintButton onClick={this.onPrint} />
        </FixedActionBar>
        }
      </WrapperPage>
    );
  }
}

function mapStateToProps(state) {
  return {
    token: getToken(state),
    radio: getRadio(state),
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({}, dispatch),
  };
}

export default translate('history')(connect(mapStateToProps, mapDispatchToProps)(HistoryPage));



// WEBPACK FOOTER //
// ./src/statsradio/modules/history/HistoryPage.js